import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation, emailValidation } from 'validators';
import { Button, PasswordField, TextField, Typography } from 'seagull';
import { routes } from '~/constants';
import { useIoContext, useStoreActions } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { LoginMethod, useSendLoginGtmEvents } from '~/hooks/useSendLoginGtmEvents';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { handleRequest } from '~/infra/utils';
import { FormSeparatorHorizontal } from '~/pages/unAuthorizedPages/components/FormSeparator';
import { useLocationCommonActions } from '~/providers/LocationProvider';
import { ErrorMessage, FooterLink, FormContainer, FormFields, VerticalFormFields, FormSubTitle, SubmitButtonField, LinkButton } from '../../components/FormComponents';
import sharedStyles from '../LoginScreen.module.scss';
import styles from './Password.module.scss';
const genericErrorMessage = 'Failed to login, please try again';
export default function PasswordStep(param) {
    let { email, onClickRequestOTP, titleMP, subtitleMP } = param;
    const [errorMessage, setErrorMessage] = useState();
    const { handleSubmit, control, formState, setFocus } = useForm({
        mode: 'onChange'
    });
    const { api, logBI } = useStoreActions();
    const [isLoading, setIsLoading] = useState(false);
    const { reportFormSubmission } = useEventReporter();
    const getUtms = useUtmsParams();
    const { getPageUrl } = useLocationCommonActions();
    const { locationReload } = useIoContext();
    const { sendGtmLoginSuccessEvent, sendGtmLoginFailEvent } = useSendLoginGtmEvents();
    async function onPasswordFormSubmit(param) {
        let { email, password } = param;
        logBI(10254, {});
        setErrorMessage(undefined);
        setIsLoading(true);
        const result = await handleRequest(api.auth.loginWithPassword({
            email,
            password,
            utms: getUtms(),
            anchorReferrer: getPageUrl()
        }));
        if (!result.success || !result.data.authToken) {
            var _result_error;
            if (((_result_error = result.error) === null || _result_error === void 0 ? void 0 : _result_error.body) === 'failed to login auth service') {
                setErrorMessage('Failed to login, please try again or reset your password');
            } else {
                setErrorMessage(genericErrorMessage);
            }
            sendGtmLoginFailEvent(LoginMethod.Password);
            setIsLoading(false);
        } else {
            reportFormSubmission('general-logged-in-successfully', null);
            await sendGtmLoginSuccessEvent(LoginMethod.Password);
            locationReload();
        }
    }
    useEffect(()=>{
        setFocus('password');
    }, []);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs(FormContainer, {
                onSubmit: handleSubmit(onPasswordFormSubmit),
                "data-testid": "login-form",
                children: [
                    titleMP,
                    subtitleMP,
                    /*#__PURE__*/ _jsx(FormFields, {
                        children: /*#__PURE__*/ _jsxs(VerticalFormFields, {
                            children: [
                                /*#__PURE__*/ _jsx(Controller, {
                                    control: control,
                                    name: "email",
                                    defaultValue: email,
                                    rules: emailValidation(undefined, {
                                        required: true
                                    }),
                                    render: (param)=>{
                                        let { field: { onChange, onBlur, ref, value }, fieldState } = param;
                                        var _fieldState_error;
                                        return /*#__PURE__*/ _jsx(TextField, {
                                            collapseHelperText: false,
                                            type: "email",
                                            label: "Work email",
                                            inputRef: ref,
                                            error: fieldState.isTouched && !!fieldState.error,
                                            helperText: fieldState.isTouched && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                            testId: "email-input",
                                            value: value,
                                            onBlur: onBlur,
                                            onChange: onChange,
                                            placeholder: "Work email",
                                            disabled: isLoading,
                                            autoComplete: "username"
                                        });
                                    }
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "password",
                                            defaultValue: "",
                                            rules: requiredValidation(true),
                                            render: (param)=>{
                                                let { field: { onChange, onBlur, ref, value }, fieldState } = param;
                                                var _fieldState_error;
                                                return /*#__PURE__*/ _jsx(PasswordField, {
                                                    label: "Password",
                                                    inputRef: ref,
                                                    value: value,
                                                    testId: "password-input",
                                                    error: !!fieldState.error,
                                                    helperText: (_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message,
                                                    onBlur: onBlur,
                                                    onChange: onChange,
                                                    placeholder: "Password",
                                                    disabled: isLoading,
                                                    autoComplete: "current-password"
                                                });
                                            }
                                        }),
                                        /*#__PURE__*/ _jsxs(Typography, {
                                            className: styles.forgotYourPassword,
                                            as: "p",
                                            variant: "paragraph2",
                                            skin: "secondary",
                                            children: [
                                                "Forgot your password?",
                                                ' ',
                                                /*#__PURE__*/ _jsx(FooterLink, {
                                                    toInternalLink: routes.resetPassword,
                                                    testId: "reset-password-link",
                                                    children: "Reset it here"
                                                })
                                            ]
                                        }),
                                        errorMessage && /*#__PURE__*/ _jsx(ErrorMessage, {
                                            testId: "err-message",
                                            children: errorMessage
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SubmitButtonField, {
                                    disabled: !formState.isValid,
                                    isLoading: isLoading,
                                    testId: "login-button",
                                    children: "Sign in"
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(FormSeparatorHorizontal, {
                children: "Or"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.otpContainer,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        testId: "request-otp-button",
                        skin: "primaryOutline",
                        onClick: ()=>onClickRequestOTP(),
                        children: "Get a one-time code"
                    }),
                    /*#__PURE__*/ _jsx(FormSubTitle, {
                        children: "We'll email you a six digit code to sign in without a password."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: sharedStyles.footer,
                children: /*#__PURE__*/ _jsx(LinkButton, {
                    toInternalLink: routes.login,
                    testId: "back-link",
                    className: sharedStyles.backButton,
                    children: "Back"
                })
            })
        ]
    });
}
